import Typography from '@components/v2/ui/Typography/Typography';
import { Checkbox, MenuItem, checkboxClasses } from '@mui/material';
import { memo } from 'react';
import { makeStyles } from 'tss-react/mui';
import LangEnum from '../../../../../../lang/LangEnum';
import { ProductDetailsFilterDeliveryWindow } from '../../../../../../services/modules/productDetails/productDetails.types';
import useTeceoStyles from '../../../../../../useTeceoStyles';
import { useTranslation } from '../../../../../translation/TranslationContext';

interface DeliveryWindowsMenuContentProps {
  deliveryWindows?: ProductDetailsFilterDeliveryWindow[];
  localSelectedDeliveryWindows: string[];
  onToggleDeliveryWindow: (deliveryWindowId: string) => void;
  handleToggleAllWindows: () => void;
}

const MENU_ITEM_MIN_WIDTH = 160;
const CHECKBOX_SIZE = 11;
const SCROLLABLE_CONTAINER_MAX_HEIGHT = 158;

const useStyles = makeStyles()((theme) => ({
  menuItem: {
    minWidth: MENU_ITEM_MIN_WIDTH,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
  },
  selectedItem: {
    backgroundColor: theme.palette.action.selected,
  },
  checkbox: {
    padding: 0,
    width: CHECKBOX_SIZE,
    height: CHECKBOX_SIZE,
    color: theme.palette.custom.border.primary,
    [`&.${checkboxClasses.checked}`]: {
      color: theme.palette.primary.main,
    },
  },
  divider: {
    marginBlock: theme.spacing(0.5),
    marginInline: 0,
    height: 1,
    border: 0,
    backgroundColor: theme.palette.custom.border.secondary,
  },
  scrollableContainer: {
    maxHeight: SCROLLABLE_CONTAINER_MAX_HEIGHT,
    overflowY: 'auto',
  },
}));

const DeliveryWindowsMenuContent = ({
  deliveryWindows,
  localSelectedDeliveryWindows,
  onToggleDeliveryWindow,
  handleToggleAllWindows,
}: DeliveryWindowsMenuContentProps) => {
  const { classes, cx } = useStyles();
  const { classes: teceoClasses } = useTeceoStyles();
  const { translate } = useTranslation();
  const isAllDeliveryWindowsSelected =
    deliveryWindows?.length === localSelectedDeliveryWindows.length;

  return (
    <div className={cx(teceoClasses.hideScrollbar, classes.scrollableContainer)}>
      <MenuItem
        key="allDeliveryWindows"
        className={cx(classes.menuItem, {
          [classes.selectedItem]: isAllDeliveryWindowsSelected,
        })}
        onClick={handleToggleAllWindows}
      >
        <Checkbox
          className={classes.checkbox}
          size="small"
          checked={isAllDeliveryWindowsSelected}
        />
        <Typography variant="body2" color="text.primary">
          {translate(LangEnum.PRODUCT_DETAILS_AVAILABILITY_FILTER_LABEL)}
        </Typography>
      </MenuItem>

      {!!deliveryWindows?.length && <hr className={classes.divider} />}

      {deliveryWindows?.map((deliveryWindow) => {
        const isSelected =
          localSelectedDeliveryWindows.includes(deliveryWindow.id) || isAllDeliveryWindowsSelected;

        return (
          <MenuItem
            key={deliveryWindow.id}
            className={cx(classes.menuItem, { [classes.selectedItem]: isSelected })}
            onClick={() => onToggleDeliveryWindow(deliveryWindow.id)}
          >
            <Checkbox className={classes.checkbox} size="small" checked={isSelected} />
            <Typography variant="body2" color="text.primary">
              {deliveryWindow.name}
            </Typography>
          </MenuItem>
        );
      })}
    </div>
  );
};

export default memo(DeliveryWindowsMenuContent);
